import React, { useState, useEffect } from 'react';
import { Cookie, X } from 'lucide-react';
import { Button } from './ui/button';
import { initGA, removeGACookies } from '../utils/analytics';

const CookieConsent = () => {
  const [showBanner, setShowBanner] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      // Small delay to prevent flash on page load
      const timer = setTimeout(() => setShowBanner(true), 1000);
      return () => clearTimeout(timer);
    } else {
      // User has already consented, initialize analytics if accepted
      try {
        const parsed = JSON.parse(consent);
        if (parsed.analytics) {
          initGA();
        }
      } catch (e) {
        console.error('Error parsing cookie consent:', e);
      }
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookie-consent', JSON.stringify(allAccepted));
    setShowBanner(false);
    
    // Initialize Google Analytics
    initGA();
  };

  const handleRejectAll = () => {
    const allRejected = {
      necessary: true, // Necessary cookies are always enabled
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookie-consent', JSON.stringify(allRejected));
    setShowBanner(false);
    
    // Remove any existing GA cookies
    removeGACookies();
  };

  const handleSavePreferences = () => {
    const savedPreferences = {
      ...preferences,
      timestamp: new Date().toISOString(),
    };
    localStorage.setItem('cookie-consent', JSON.stringify(savedPreferences));
    setShowBanner(false);
    setShowPreferences(false);
    
    // Initialize or remove GA based on preference
    if (preferences.analytics) {
      initGA();
    } else {
      removeGACookies();
    }
  };

  const togglePreference = (key) => {
    if (key === 'necessary') return; // Cannot toggle necessary cookies
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  if (!showBanner) return null;

  return (
    <div 
      className="fixed bottom-0 left-0 right-0 z-[100] p-4 md:p-6"
      data-testid="cookie-consent-banner"
    >
      <div className="max-w-4xl mx-auto">
        <div className="rounded-2xl bg-slate-900 border border-white/10 shadow-2xl overflow-hidden">
          {!showPreferences ? (
            // Main Banner
            <div className="p-6">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <Cookie className="text-blue-400" size={24} />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-white mb-2">
                    We value your privacy
                  </h3>
                  <p className="text-sm text-slate-400 mb-4">
                    We use cookies to enhance your browsing experience and analyze our traffic. 
                    By clicking "Accept All", you consent to our use of cookies as described in our{' '}
                    <a href="/privacy-policy" className="text-blue-400 hover:text-blue-300 underline">
                      Privacy Policy
                    </a>.
                  </p>
                  <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                    <Button
                      onClick={handleAcceptAll}
                      className="bg-blue-600 hover:bg-blue-500 text-white rounded-full px-6"
                      data-testid="cookie-accept-all"
                    >
                      Accept All
                    </Button>
                    <Button
                      onClick={handleRejectAll}
                      variant="outline"
                      className="border-white/20 text-white hover:bg-white/10 rounded-full px-6"
                      data-testid="cookie-reject-all"
                    >
                      Reject All
                    </Button>
                    <button
                      onClick={() => setShowPreferences(true)}
                      className="text-sm text-slate-400 hover:text-white transition-colors underline"
                      data-testid="cookie-customize"
                    >
                      Customize
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            // Preferences Panel
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-white">Cookie Preferences</h3>
                <button
                  onClick={() => setShowPreferences(false)}
                  className="text-slate-400 hover:text-white transition-colors"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="space-y-4 mb-6">
                {/* Necessary Cookies */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex-1">
                    <h4 className="text-white font-medium mb-1">Strictly Necessary</h4>
                    <p className="text-xs text-slate-500">
                      Essential for the website to function. Cannot be disabled.
                    </p>
                  </div>
                  <div className="ml-4">
                    <div className="w-12 h-6 rounded-full bg-blue-600 flex items-center justify-end px-1">
                      <div className="w-4 h-4 rounded-full bg-white"></div>
                    </div>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex-1">
                    <h4 className="text-white font-medium mb-1">Analytics</h4>
                    <p className="text-xs text-slate-500">
                      Help us understand how visitors interact with our website using Google Analytics.
                    </p>
                  </div>
                  <button
                    onClick={() => togglePreference('analytics')}
                    className="ml-4"
                    data-testid="cookie-toggle-analytics"
                  >
                    <div className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                      preferences.analytics ? 'bg-blue-600 justify-end' : 'bg-slate-700 justify-start'
                    }`}>
                      <div className="w-4 h-4 rounded-full bg-white"></div>
                    </div>
                  </button>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-center justify-between p-4 rounded-xl bg-white/5 border border-white/10">
                  <div className="flex-1">
                    <h4 className="text-white font-medium mb-1">Marketing</h4>
                    <p className="text-xs text-slate-500">
                      Used to deliver personalized advertisements.
                    </p>
                  </div>
                  <button
                    onClick={() => togglePreference('marketing')}
                    className="ml-4"
                    data-testid="cookie-toggle-marketing"
                  >
                    <div className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                      preferences.marketing ? 'bg-blue-600 justify-end' : 'bg-slate-700 justify-start'
                    }`}>
                      <div className="w-4 h-4 rounded-full bg-white"></div>
                    </div>
                  </button>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                <Button
                  onClick={handleSavePreferences}
                  className="bg-blue-600 hover:bg-blue-500 text-white rounded-full px-6"
                  data-testid="cookie-save-preferences"
                >
                  Save Preferences
                </Button>
                <Button
                  onClick={handleAcceptAll}
                  variant="outline"
                  className="border-white/20 text-white hover:bg-white/10 rounded-full px-6"
                >
                  Accept All
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CookieConsent;
